<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import TransactionForm from "$lib/components/TransactionForm.svelte";
  import EmptyState from "$lib/components/EmptyState.svelte";
  import PaywallModal from "$lib/components/PaywallModal.svelte";
  import Modal from "$lib/components/Modal.svelte";
  import ScanReceiptModal from "$lib/components/ScanReceiptModal.svelte";
  import TelegramConfirmBanner from "$lib/components/TelegramConfirmBanner.svelte";
  import { categories } from "$lib/stores/categories.svelte";
  import { auth } from "$lib/stores/auth.svelte";
  import { api } from "$lib/api/client";
  import { toasts } from "$lib/stores/toasts.svelte";
  import { fmtBRL, fmtDateBR } from "$lib/utils/format";

  // ── Navegador de mês ─────────────────────────────────────
  const today = new Date();
  let selYear  = $state(today.getFullYear());
  let selMonth = $state(today.getMonth()); // 0-based

  const MONTHS = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho",
                  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

  function prevMonth() {
    if (selMonth === 0) { selMonth = 11; selYear--; } else { selMonth--; }
  }
  function nextMonth() {
    if (selMonth === 11) { selMonth = 0; selYear++; } else { selMonth++; }
  }

  function dateRange(year: number, month: number) {
    const from = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const last = new Date(year, month + 1, 0).getDate();
    const to   = `${year}-${String(month + 1).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
    return { from, to };
  }

  // ── Estado ───────────────────────────────────────────────
  let txs     = $state<any[]>([]);
  let cursor  = $state<string | null>(null);
  let loading = $state(false);
  let initialLoaded = $state(false);
  let showForm = $state(false);

  let editing  = $state<any | null>(null);
  let editOpen = $state(false);

  // Scope dialog (recorrências)
  type ScopeAction = "delete" | "edit";
  let scopeTx      = $state<any | null>(null);
  let scopeAction  = $state<ScopeAction>("delete");
  let scopeOpen    = $state(false);
  let scopeChoice  = $state<"one" | "future" | "all">("one");

  let kindFilter   = $state<"all" | "income" | "expense" | "transfer">("all");
  let statusFilter = $state<"all" | "pending" | "effected" | "cancelled">("all");
  let search = $state("");

  let paywallOpen = $state(false);
  let scanOpen    = $state(false);

  // Escuta evento do Dashboard "filtrar pendentes"
  $effect(() => {
    function onFilterPending() { statusFilter = "pending"; }
    window.addEventListener("takite:filter-pending", onFilterPending);
    return () => window.removeEventListener("takite:filter-pending", onFilterPending);
  });

  let members = $state<Record<string, string>>({});  // user_id → name (family)
  const isFamily = $derived(auth.workspaces[0]?.type === "family");

  async function load(reset = true) {
    loading = true;
    try {
      const { from, to } = dateRange(selYear, selMonth);
      const params = new URLSearchParams({ limit: "200", date_from: from, date_to: to });
      if (!reset && cursor) params.set("cursor", cursor);
      const r = await api<{ items: any[]; next_cursor: string | null }>(
        `/transactions?${params.toString()}`,
      );
      txs = reset ? r.items : [...txs, ...r.items];
      cursor = r.next_cursor;
      initialLoaded = true;
      // Para workspaces família, carregar mapa de membros (se não carregado)
      const ws = auth.workspaces[0];
      if (ws?.type === "family" && Object.keys(members).length === 0) {
        try {
          const mdata = await api<{ members: { user_id: string; name: string }[] }>(`/workspaces/${ws.id}/member-spending`);
          const map: Record<string, string> = {};
          for (const m of mdata.members) map[m.user_id] = m.name;
          members = map;
        } catch { /* ignore */ }
      }
    } finally { loading = false; }
  }

  $effect(() => { load(true); categories.load(); });

  // Recarrega ao mudar de mês — selYear/selMonth são lidos dentro de load()
  // portanto não precisam ser citados explicitamente (Svelte rastreia automaticamente)
  $effect(() => {
    function onWsChange() { load(true); categories.load(); }
    window.addEventListener("takite:workspace-changed", onWsChange);
    return () => window.removeEventListener("takite:workspace-changed", onWsChange);
  });

  let filtered = $derived(txs.filter((t) => {
    if (kindFilter !== "all" && t.kind !== kindFilter) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (search.trim() && !t.description.toLowerCase().includes(search.trim().toLowerCase())) return false;
    return true;
  }));

  // Totais do mês (sobre transações filtradas por mês, antes dos filtros de exibição)
  let monthTotals = $derived(() => {
    let income = 0, expense = 0;
    for (const t of txs) {
      const v = Number(t.amount);
      if (t.kind === "income") income += v;
      else if (t.kind === "expense") expense += v;
    }
    return { income, expense };
  });

  async function markPaid(id: string) {
    try {
      await api(`/transactions/${id}/effect`, { method: "PATCH", body: "{}" });
      toasts.success("Transação marcada como paga");
      load(true);
    } catch (e: any) {
      if (e.body?.code === "pair_incomplete") {
        toasts.warn("Anexo necessário", `Faltando: ${e.body.missing.join(", ")}`);
      } else {
        toasts.error("Não foi possível efetivar", e.message);
      }
    }
  }

  function askScope(t: any, action: ScopeAction) {
    if (t.recurrence_id) {
      scopeTx = t; scopeAction = action; scopeChoice = "one"; scopeOpen = true;
    } else if (action === "delete") {
      doDelete(t.id, "one");
    } else {
      editing = t; editOpen = true;
    }
  }

  async function doDelete(id: string, scope: string) {
    try {
      await api(`/transactions/${id}?scope=${scope}`, { method: "DELETE" });
      toasts.success("Removida");
      txs = txs.filter((t) => t.id !== id);
    } catch (e: any) { toasts.error("Erro ao remover", e.message); }
  }

  async function confirmScope() {
    if (!scopeTx) return;
    if (scopeAction === "delete") {
      await doDelete(scopeTx.id, scopeChoice);
    } else {
      editing = { ...scopeTx, _scope: scopeChoice };
      editOpen = true;
    }
    scopeOpen = false; scopeTx = null;
  }

  // Mantém compatibilidade: remove direto (sem recorrência) e openEdit antigo
  function openEdit(t: any) { askScope(t, "edit"); }
  async function remove(t: any) { askScope(t, "delete"); }

  function statusBadge(s: string) {
    return s === "effected" ? "badge-effected" : s === "pending" ? "badge-pending" : "badge-cancelled";
  }
  function statusLabel(s: string) {
    return s === "effected" ? "Paga" : s === "pending" ? "Pendente" : "Cancelada";
  }
  function kindIcon(k: string) {
    return k === "income" ? "💰" : k === "expense" ? "💸" : "🔁";
  }
  function catLabel(id?: string | null) {
    const c = categories.byId(id);
    return c ? `${c.icon ?? ""} ${c.name}` : "—";
  }
</script>

<Navbar />
<TelegramConfirmBanner onConfirmed={() => load(true)} />
<main class="max-w-7xl mx-auto px-4 py-6 space-y-4">

  <!-- Cabeçalho + navegador de mês -->
  <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
    <h1 class="text-2xl font-bold">Transações</h1>
    <div class="flex items-center gap-2 flex-wrap">
      <!-- Totais rápidos do mês -->
      {#if !loading && txs.length > 0}
        <span class="text-xs text-muted hidden sm:inline">
          <span class="text-emerald-500 font-semibold">{fmtBRL(monthTotals().income)}</span>
          <span class="mx-1 text-muted">·</span>
          <span class="text-red-500 font-semibold">{fmtBRL(monthTotals().expense)}</span>
        </span>
      {/if}
      <!-- Navegador de mês -->
      <button class="btn-ghost px-2 py-1 text-lg" onclick={prevMonth} aria-label="Mês anterior">‹</button>
      <span class="font-semibold text-sm min-w-[130px] text-center">
        {MONTHS[selMonth]} {selYear}
      </span>
      <button class="btn-ghost px-2 py-1 text-lg" onclick={nextMonth} aria-label="Próximo mês">›</button>
      <button class="btn-primary ml-1" onclick={() => (showForm = !showForm)}>
        {showForm ? "Fechar" : "+ Nova"}
      </button>
    </div>
  </div>

  {#if showForm}
    <TransactionForm onCreated={() => { showForm = false; load(true); }} />
  {/if}

  <!-- Filtros -->
  <div class="card flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
    <div class="flex-1">
      <label class="label" for="srch">Buscar</label>
      <input id="srch" class="input" placeholder="Descrição…" bind:value={search} />
    </div>
    <div>
      <label class="label" for="fk">Tipo</label>
      <select id="fk" class="input" bind:value={kindFilter}>
        <option value="all">Todos</option>
        <option value="expense">Despesas</option>
        <option value="income">Receitas</option>
        <option value="transfer">Transferências</option>
      </select>
    </div>
    <div>
      <label class="label" for="fs">Situação</label>
      <select id="fs" class="input" bind:value={statusFilter}>
        <option value="all">Todas</option>
        <option value="pending">Pendentes</option>
        <option value="effected">Pagas</option>
        <option value="cancelled">Canceladas</option>
      </select>
    </div>
  </div>

  {#if loading && !initialLoaded}
    <div class="space-y-2">
      {#each [1, 2, 3, 4, 5] as _}<div class="skeleton h-14"></div>{/each}
    </div>
  {:else if filtered.length === 0}
    <EmptyState icon="📭"
                title={txs.length === 0 ? `Nenhuma transação em ${MONTHS[selMonth]}` : "Nada com esses filtros"}
                description={txs.length === 0
                  ? "Comece registrando seu primeiro lançamento."
                  : "Ajuste os filtros ou registre uma nova transação."}
                actionLabel={txs.length === 0 ? "+ Nova transação" : undefined}
                onAction={() => (showForm = true)} />
  {:else}
    <!-- Mobile: cards -->
    <div class="md:hidden space-y-2">
      {#each filtered as t}
        <div class="tx-row-mobile">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span class="text-xl" aria-hidden="true">{kindIcon(t.kind)}</span>
              <div>
                <div class="font-medium flex items-center gap-1">
                  {#if t.privacy === "owner_only"}<span title="Privada" aria-label="Privada">🔒</span>{/if}
                  <span>{t.description}</span>
                </div>
                <div class="text-xs text-muted">
                  {#if t.due_date}⏰ vence {fmtDateBR(t.due_date)} · {/if}{catLabel(t.category_id)}{#if isFamily && t.created_by && members[t.created_by]} · <span class="text-indigo-500 font-medium">{members[t.created_by]}</span>{/if}
                </div>
              </div>
            </div>
            <span class="font-semibold" class:text-emerald-600={t.kind === "income"}
                  class:text-red-600={t.kind === "expense"}>{fmtBRL(t.amount)}</span>
          </div>
          <div class="flex items-center justify-between mt-1">
            <span class="badge {statusBadge(t.status)}">{statusLabel(t.status)}</span>
            <div class="flex gap-1">
              {#if t.status === "pending"}
                <button
                  class="text-xs font-semibold px-3 py-1 rounded-full text-white"
                  style="background: #16a34a;"
                  onclick={() => markPaid(t.id)}
                >✅ Marcar paga</button>
              {/if}
              <button class="btn-ghost text-sm" aria-label="Editar" title="Editar" onclick={() => openEdit(t)}>✏️</button>
              <button class="btn-ghost text-sm text-red-600" aria-label="Remover" onclick={() => remove(t)}>🗑️</button>
            </div>
          </div>
        </div>
      {/each}
    </div>

    <!-- Desktop: tabela -->
    <div class="hidden md:block card overflow-x-auto">
      <table class="w-full text-sm">
        <thead class="text-left text-muted">
          <tr class="border-b" style="border-color: var(--border)">
            <th class="py-2 pr-2">Vencimento</th>
            <th class="pr-2">Descrição</th>
            <th class="pr-2">Categoria</th>
            {#if isFamily}<th class="pr-2">Quem</th>{/if}
            <th class="pr-2 text-right">Valor</th>
            <th class="pr-2">Situação</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each filtered as t}
            <tr class="border-b last:border-0" style="border-color: var(--border)">
              <td class="py-2 pr-2 whitespace-nowrap">
                {fmtDateBR(t.due_date || t.date)}
              </td>
              <td class="pr-2">{#if t.privacy === "owner_only"}<span title="Privada">🔒 </span>{/if}{t.description}</td>
              <td class="pr-2 text-muted">{catLabel(t.category_id)}</td>
              {#if isFamily}<td class="pr-2 text-indigo-500 text-xs font-medium">{members[t.created_by] ?? "—"}</td>{/if}
              <td class="pr-2 text-right font-semibold whitespace-nowrap"
                  class:text-emerald-600={t.kind === "income"} class:text-red-600={t.kind === "expense"}>
                {fmtBRL(t.amount)}
              </td>
              <td class="pr-2"><span class="badge {statusBadge(t.status)}">{statusLabel(t.status)}</span></td>
              <td class="text-right whitespace-nowrap">
                {#if t.status === "pending"}
                  <button
                    class="text-xs font-semibold px-3 py-1 rounded-full text-white mr-1"
                    style="background: #16a34a;"
                    onclick={() => markPaid(t.id)}
                  >✅ Pagar</button>
                {/if}
                <button class="btn-ghost text-sm" aria-label="Editar" title="Editar" onclick={() => openEdit(t)}>✏️</button>
                <button class="btn-ghost text-sm text-red-600" aria-label="Remover" onclick={() => remove(t)}>🗑️</button>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    {#if cursor}
      <div class="text-center pt-2">
        <button class="btn-secondary" onclick={() => load(false)} disabled={loading}>
          {loading ? "Carregando…" : "Carregar mais"}
        </button>
      </div>
    {/if}
  {/if}
</main>

<PaywallModal bind:open={paywallOpen} reason="transactions" />

<Modal bind:open={editOpen} title="Editar transação">
  {#if editing}
    <TransactionForm
      edit={editing}
      scope={editing._scope ?? "one"}
      onCreated={() => { editOpen = false; editing = null; load(true); }}
    />
  {/if}
</Modal>

<!-- ── Scope Dialog (recorrências/parcelas) ───────────────────────────── -->
{#if scopeOpen && scopeTx}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-sm w-full p-6 space-y-4">
      <h2 class="font-semibold text-lg">
        {scopeAction === "delete" ? "🗑️ Remover" : "✏️ Editar"} transação recorrente
      </h2>
      <p class="text-sm text-slate-500">
        Esta transação faz parte de uma série.
        {scopeAction === "delete" ? "Quais deseja remover?" : "Quais deseja alterar?"}
      </p>
      <div class="space-y-2">
        {#each [
          { value: "one",    label: "Só esta",                desc: "Apenas este lançamento" },
          { value: "future", label: "Esta e as futuras",      desc: "Este e todos os próximos da série" },
          { value: "all",    label: "Todas da série",         desc: "Passados, presente e futuros" },
        ] as opt}
          <label class="flex items-start gap-3 cursor-pointer rounded-lg border p-3 transition-colors
            {scopeChoice === opt.value
              ? 'border-brand bg-brand/5 dark:bg-brand/10'
              : 'border-slate-200 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700/50'}">
            <input type="radio" bind:group={scopeChoice} value={opt.value} class="mt-0.5 accent-brand" />
            <div>
              <div class="font-medium text-sm">{opt.label}</div>
              <div class="text-xs text-slate-400">{opt.desc}</div>
            </div>
          </label>
        {/each}
      </div>
      <div class="flex gap-2 pt-1">
        <button class="btn-ghost flex-1" onclick={() => { scopeOpen = false; scopeTx = null; }}>Cancelar</button>
        <button
          class="flex-1 rounded-lg py-2 font-semibold text-sm text-white transition-colors
            {scopeAction === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-brand hover:bg-brand/90'}"
          onclick={confirmScope}
        >
          {scopeAction === "delete" ? "Remover" : "Continuar"}
        </button>
      </div>
    </div>
  </div>
{/if}

<ScanReceiptModal bind:open={scanOpen} onCreated={() => load(true)} />

<!-- FAB: digitalizar nota -->
<button
  type="button"
  class="fixed bottom-6 right-5 z-40 flex items-center gap-2 rounded-full shadow-xl px-5 py-3 font-semibold text-sm transition-transform active:scale-95 sm:bottom-8 sm:right-8"
  style="background: var(--brand); color: #fff; box-shadow: 0 4px 24px rgba(0,0,0,0.25);"
  title="Digitalizar nota ou cupom"
  aria-label="Digitalizar nota com IA"
  onclick={() => (scanOpen = true)}
>
  <span aria-hidden="true">📸</span>
  <span class="hidden sm:inline">Digitalizar nota</span>
  <span class="sm:hidden">Nota</span>
</button>
